import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Briefcase, ArrowLeft, Video, Calendar, Users, Clock, CheckCircle2, Lock, DollarSign, Play, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Webinars() {
  const { user } = useAuth();
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/webinars`).then(r => { setWebinars(r.data.webinars); setLoading(false); }).catch(console.error);
  }, []);

  const register = async (webinarId) => {
    if (!user) { window.location.href = '/auth'; return; }
    try {
      const { data } = await axios.post(`${API}/webinars/${webinarId}/register`, {}, { withCredentials: true });
      alert(data.message);
      const res = await axios.get(`${API}/webinars`);
      setWebinars(res.data.webinars);
    } catch (err) { alert(err.response?.data?.detail || 'Registration failed'); }
  };

  const filtered = filter === 'all' ? webinars : webinars.filter(w => w.status === filter);
  const TOPIC_COLORS = { platform: 'bg-[#047857]/10 text-[#047857]', technical: 'bg-[#D97757]/10 text-[#D97757]', career: 'bg-[#86A789]/20 text-[#1C2A24]', general: 'bg-[#F4F1E8] text-[#1C2A24]/60' };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="glass sticky top-0 z-50 border-b border-[#E5E0D8]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#047857] flex items-center justify-center"><Briefcase size={14} className="text-white" strokeWidth={1.5} /></div>
            <span className="text-lg font-semibold tracking-tight" style={{fontFamily:'Outfit'}}>WorkFromJoy</span>
          </Link>
        </div>
      </nav>
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-[#047857] mb-4"><ArrowLeft size={14} strokeWidth={1.5} /> Dashboard</Link>
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl tracking-tight font-medium" style={{fontFamily:'Outfit'}}>Webinars & Live Sessions</h1>
          <p className="text-[#1C2A24]/60 mt-2">Join live workshops, Q&A sessions, and expert-led training to boost your WFH career</p>
        </div>

        {/* TikTok Banner */}
        <a href="https://www.tiktok.com/@workfromjoyhub" target="_blank" rel="noopener noreferrer" className="block mb-6" data-testid="tiktok-banner">
          <div className="bg-[#1C2A24] rounded-2xl p-4 flex items-center justify-between hover:opacity-90 transition-opacity">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 0012.49-1.53V8.15a8.16 8.16 0 004.74 1.52v-3.4a4.85 4.85 0 01-.79.42z"/></svg>
              </div>
              <div>
                <p className="text-white text-sm font-medium">Follow us on TikTok @workfromjoyhub</p>
                <p className="text-white/50 text-xs">Daily WFH tips, job alerts & motivation</p>
              </div>
            </div>
            <ExternalLink size={16} className="text-white/50" strokeWidth={1.5} />
          </div>
        </a>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="webinar-filters">
          {['all', 'upcoming', 'live', 'completed'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all capitalize ${filter === s ? 'bg-[#047857] text-white' : 'bg-[#F4F1E8] text-[#1C2A24]/70 hover:bg-[#E5E0D8]'}`}>
              {s === 'all' ? 'All Sessions' : s}
            </button>
          ))}
        </div>

        {/* Webinar Cards */}
        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-40 bg-white rounded-3xl animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-3xl border border-[#E5E0D8] p-12 text-center">
            <Video size={48} className="text-[#E5E0D8] mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-[#1C2A24]/50">No webinars found for this filter.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="webinar-list">
            {filtered.map(w => (
              <div key={w.id} className="bg-white rounded-3xl border border-[#E5E0D8] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(4,120,87,0.06)] transition-all" data-testid={`webinar-${w.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {w.status === 'live' && <span className="flex items-center gap-1 text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium"><span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>LIVE</span>}
                      {w.status === 'upcoming' && <span className="text-[10px] bg-[#A7F3D0]/30 text-[#047857] px-2 py-0.5 rounded-full">Upcoming</span>}
                      {w.status === 'completed' && <span className="text-[10px] bg-[#F4F1E8] text-[#1C2A24]/50 px-2 py-0.5 rounded-full">Recording Available</span>}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${TOPIC_COLORS[w.topic] || TOPIC_COLORS.general}`}>{w.topic}</span>
                      {!w.is_free && <span className="text-[10px] bg-[#D97757]/10 text-[#D97757] px-2 py-0.5 rounded-full font-medium">${w.price} Premium</span>}
                      {w.is_free && <span className="text-[10px] bg-[#A7F3D0]/30 text-[#047857] px-2 py-0.5 rounded-full">Free</span>}
                    </div>
                    <h3 className="text-lg font-medium mb-1" style={{fontFamily:'Outfit'}}>{w.title}</h3>
                    <p className="text-sm text-[#1C2A24]/60 mb-3">{w.description}</p>
                    <div className="flex items-center gap-4 text-xs text-[#1C2A24]/50">
                      <span className="flex items-center gap-1"><Calendar size={12} strokeWidth={1.5} />{w.scheduled_at}</span>
                      <span className="flex items-center gap-1"><Users size={12} strokeWidth={1.5} />{w.attendee_count}/{w.max_attendees} registered</span>
                    </div>
                  </div>
                  <div className="ml-4 shrink-0">
                    {w.status === 'completed' && w.recording_url ? (
                      <a href={w.recording_url} target="_blank" rel="noopener noreferrer">
                        <Button className="rounded-full bg-[#D97757] hover:bg-[#C4623F] text-white" data-testid={`watch-${w.id}`}>
                          <Play size={14} className="mr-1" /> Watch Recording
                        </Button>
                      </a>
                    ) : (
                      <Button onClick={() => register(w.id)} className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white" data-testid={`register-${w.id}`}>
                        <CheckCircle2 size={14} className="mr-1" /> Register
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
